// Modules
import { useMemo, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import isEqual from "lodash.isequal";
// Material-UI
import { Box, Button, Dialog } from "@mui/material";
// Calendar External Lib
import FullCalendar from "@fullcalendar/react";
import listPlugin from "@fullcalendar/list";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import timelinePlugin from "@fullcalendar/timeline";
import interactionPlugin from "@fullcalendar/interaction";
import rrulePlugin from "@fullcalendar/rrule";
import allLocales from "@fullcalendar/core/locales-all";
// Components
import MainCard from "../../cards/MainCard";
import CalendarStyled from "./CalendarStyled";
import Toolbar from "./Toolbar";
import EventDetails from "./EventDetails";
import EditEvent from "./EditEvent";
import { CalendarCreateButton } from "./components/CalendarCreateButton";
import ColorMenu from "./components/ColorMenu";
// Helpers
import { getLocaleByLang } from "../../../../helpers/locales";
import {
    EVENT_SESSION,
    EVENT_FREE_SLOT,
    THERAPIST_ABSENCE_EVENT_TYPES,
    EVENT_PUBLIC_HOLIDAY,
    EVENT_COMPANY_HOLIDAY
} from "../../../../helpers/constants";
import {
    buildBatchTherapistSelection,
    eventContent,
    extractEventData,
    prepareEventsForCalendar
} from "./helpers/calendar";
// Assets
import AddAlarmTwoToneIcon from "@mui/icons-material/AddAlarmTwoTone";
import { getRole } from "@/helpers/userData";
import type { BaseCalendarProps, CreateTherapistAbsenceProps } from "@/lib/types/therapist/calendar";
import { useAppSelector } from "@/hooks/useAppSelector";

function updateCreationType(state, eventTypeCreation, eventToSet) {
    if (eventTypeCreation.includes(eventToSet)) {
        state(eventToSet);
    }
}

const BaseCalendar = ({
    createEventAction,
    updateEventAction,
    deleteEventAction,
    events: eventsList = [],
    eventTypeCreation,
    isAdmin,
    calendarProps = false,
    permissions = [],
    bookEventFormProps = {},
    handleDatesSet = null,
    therapistData,
    therapistsList,
    selectedTherapists,
    therapistSelectionHook,
    batchTherapistSelection,
    createTherapistAbsence,
    updateTherapistAbsence,
    deleteTherapistAbsence,
    selectedColorIndex,
    handleColorChange
}: BaseCalendarProps) => {
    const calendarRef = useRef(null);
    const customizationLocale = useAppSelector(state => state?.customization?.locale, isEqual);
    const localeLang = customizationLocale || "de";
    const locale = getLocaleByLang(localeLang);

    const [isModalOpen, setIsModalOpen] = useState<string | boolean>(false);
    const [creationType, setCreationType] = useState();
    const [selectedEvent, setSelectedEvent] = useState(null);

    const userRole = getRole();

    const events = useMemo(() => {
        return prepareEventsForCalendar({ calendarRef, events: eventsList });
    }, [calendarRef, eventsList]);

    // calendar toolbar events
    const handleCalendarActions = (action, newView) => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();

            switch (action) {
                case "today":
                    calendarApi.today();
                    break;
                case "changeView":
                    calendarApi.changeView(newView);
                    break;
                case "prev":
                    calendarApi.prev();
                    break;
                case "next":
                    calendarApi.next();
                    break;
                default:
                    return calendarApi?.getDate();
            }
        }
    };

    const hasPermission = permission => {
        return permissions.includes(permission) || permissions.some(p => p.startsWith(permission));
    };

    // calendar event select/add/edit/delete
    const triggerCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEvent(null);

        const shouldSelectAllTherapists =
            !selectedEvent?.event && (creationType === EVENT_PUBLIC_HOLIDAY || creationType === EVENT_COMPANY_HOLIDAY);

        if (shouldSelectAllTherapists) {
            batchTherapistSelection({});
        }
    };

    // Actions
    const handleCreateEventAction = ({ data, id, successCb }) => {
        createEventAction &&
            createEventAction({
                userId: selectedEvent?.event?.user?._id || id,
                data,
                successCb: () => {
                    successCb && successCb();
                    triggerCloseModal();
                }
            });
    };

    const handleUpdateEventAction = ({ userId, eventId, params, update, successCb }) => {
        updateEventAction &&
            updateEventAction({
                userId,
                eventId,
                update,
                params,
                successCb: () => {
                    successCb && successCb();
                    triggerCloseModal();
                }
            });
    };

    const handleEventDeleteAction = (event, successCb) => {
        deleteEventAction &&
            deleteEventAction({
                userId: event?.user?._id,
                id: event?.id,
                successCb: () => {
                    successCb && successCb();
                    triggerCloseModal();
                }
            });
    };

    const createTherapistAbsenceAction = ({ data, successCb, errorCb }: CreateTherapistAbsenceProps) => {
        createTherapistAbsence &&
            createTherapistAbsence({
                data,
                successCb: () => {
                    successCb && successCb();
                    triggerCloseModal();
                },
                errorCb
            });
    };

    const updateTherapistAbsenceAction = ({ id, userId, data, successCb, errorCb }) => {
        updateTherapistAbsence &&
            updateTherapistAbsence({
                id,
                userId,
                data,
                successCb: () => {
                    successCb && successCb();
                    triggerCloseModal();
                },
                errorCb
            });
    };

    const deleteTherapistAbsenceAction = ({ id, userId, type, successCb, errorCb }) => {
        deleteTherapistAbsence &&
            deleteTherapistAbsence({
                id,
                userId,
                type,
                successCb: () => {
                    successCb && successCb();
                    triggerCloseModal();
                },
                errorCb
            });
    };

    // Functions
    const handleEventDateRangeSelect = arg => {
        const calendarEl = calendarRef.current;
        if (calendarEl) {
            const calendarApi = calendarEl.getApi();
            calendarApi.unselect();
        }

        setSelectedEvent({
            update: { start: arg.start }
        });
        setIsModalOpen("edit");
    };

    const handleEventDateSelect = arg => {
        const id = arg?.event?.id;
        if (id) {
            const { originalEvent, eventData } = extractEventData({ event: arg?.event, events: eventsList });

            setSelectedEvent({ event: originalEvent, update: eventData });
        } else {
            setSelectedEvent(null);
        }
        setIsModalOpen("view");
    };

    const handleEventDragNDrop = async ({ event, oldEvent, revert }) => {
        revert && revert();

        const { originalEvent, eventData } = extractEventData({ event, events: eventsList });

        eventData.dragDropEventStart = oldEvent.start;

        setSelectedEvent({ event: originalEvent, update: eventData });
        setIsModalOpen("edit");
    };

    // Modal actions
    const triggerUpdateEventModal = () => {
        updateCreationType(setCreationType, eventTypeCreation, EVENT_FREE_SLOT);
        setIsModalOpen("edit");
    };

    const triggerCreateAbsenceModal = type => {
        updateCreationType(setCreationType, eventTypeCreation, type);

        const shouldSelectAllTherapists =
            !selectedEvent?.event && (type === EVENT_PUBLIC_HOLIDAY || type === EVENT_COMPANY_HOLIDAY);
        if (shouldSelectAllTherapists) {
            batchTherapistSelection(buildBatchTherapistSelection(therapistsList));
        }

        setIsModalOpen("therapist-absence");
    };

    const triggerBookEventModal = () => {
        updateCreationType(setCreationType, eventTypeCreation, EVENT_SESSION);
        setIsModalOpen("book");
    };

    const triggerUpdateGroupedSessionEventModal = () => {
        updateCreationType(setCreationType, eventTypeCreation, EVENT_SESSION);
        setIsModalOpen("edit-grouped-session");
    };

    const triggerDeleteEventModal = () => {
        setIsModalOpen("delete");
    };

    return (
        <MainCard
            {...(hasPermission("add")
                ? isAdmin
                    ? {
                          title: <FormattedMessage id="calendar" />,
                          secondary: <CalendarCreateButton onMenuItemClick={triggerCreateAbsenceModal} />
                      }
                    : {
                          title: <FormattedMessage id="calendar" />,
                          secondary: (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <ColorMenu
                                      selectedColorIndex={selectedColorIndex}
                                      onColorChange={handleColorChange}
                                  />
                                  <Button
                                      color="secondary"
                                      variant="contained"
                                      onClick={triggerUpdateEventModal}
                                      data-testid="create-free-slot-event"
                                  >
                                      <AddAlarmTwoToneIcon fontSize="small" sx={{ mr: 0.75 }} />
                                      <FormattedMessage id="create-event" />
                                  </Button>
                              </Box>
                          )
                      }
                : {})}
            data-testid="calendar"
        >
            <CalendarStyled>
                {!calendarProps?.headerToolbar && (
                    <Toolbar locale={locale} calendarActions={handleCalendarActions} handleDatesSet={handleDatesSet} />
                )}
                <FullCalendar
                    // Data and Event Handling
                    ref={calendarRef}
                    events={events}
                    locale={localeLang}
                    locales={allLocales}
                    // Calendar Display Options
                    height={900}
                    initialDate={calendarProps ? calendarProps.initialDate : new Date()}
                    validRange={calendarProps ? calendarProps.validRange : false}
                    initialView={calendarProps ? calendarProps.initialView : "dayGridMonth"}
                    headerToolbar={calendarProps ? calendarProps.headerToolbar : false}
                    dayMaxEventRows={calendarProps ? calendarProps.dayMaxEventRows : 3}
                    eventDisplay="block"
                    allDaySlot={true}
                    weekNumberCalculation={"ISO"}
                    // User Interactions
                    weekends
                    editable
                    droppable
                    selectable={hasPermission("add")}
                    eventDurationEditable={false}
                    select={handleEventDateRangeSelect}
                    eventDrop={handleEventDragNDrop}
                    eventClick={handleEventDateSelect}
                    visibleRange={function (currentDate) {
                        // Generate a new date for manipulating in the next step
                        var startDate = new Date(currentDate.valueOf());
                        var endDate = new Date(currentDate.valueOf());

                        // Adjust the start & end dates, respectively
                        startDate.setDate(startDate.getDate() - 1); // One day in the past
                        endDate.setDate(endDate.getDate() + 2); // Two days into the future
                    }}
                    // Other Configurations
                    allDayMaintainDuration
                    selectAllow={e => {
                        if (!hasPermission("add")) {
                            return false;
                        }

                        updateCreationType(setCreationType, eventTypeCreation, EVENT_FREE_SLOT);

                        let endDate = e.end;

                        if (e.allDay) {
                            // Allow only maximum of one day selection
                            endDate.setDate(endDate.getDate() - 1);
                            return e.start.getDate() === endDate.getDate();
                        } else {
                            // Allow only 1 hour slot selection
                            return endDate - e.start <= 1000 * 60 * 60;
                        }
                    }}
                    eventAllow={(dropInfo, draggedEvent) => {
                        const originalEvent = events?.find(e => e.id === draggedEvent.id);

                        if (
                            !hasPermission(`edit_${originalEvent?.type}`) ||
                            THERAPIST_ABSENCE_EVENT_TYPES.includes(originalEvent?.type)
                        ) {
                            return false;
                        }

                        // restricted drag & drop feature to non recurring events
                        // or for recurring events changes only on time level
                        const newStartDate = dropInfo?.start;

                        const isInSameDay =
                            newStartDate.toISOString().split("T")[0] === draggedEvent.start.toISOString().split("T")[0];

                        return !originalEvent.rrule || isInSameDay;
                    }}
                    eventContent={eventInfo => eventContent(eventInfo, userRole)}
                    slotDuration="01:00:00"
                    plugins={[
                        listPlugin,
                        dayGridPlugin,
                        timelinePlugin,
                        timeGridPlugin,
                        interactionPlugin,
                        rrulePlugin
                    ]}
                    views={{
                        timeGridDay: {
                            dayHeaderFormat: { month: "long", year: "numeric", day: "numeric", weekday: "long" }
                        }
                    }}
                />
            </CalendarStyled>

            <Dialog
                maxWidth="sm"
                fullWidth
                onClose={triggerCloseModal}
                open={!!isModalOpen}
                sx={{ "& .MuiDialog-paper": { p: 0 } }}
            >
                {["book", "edit", "edit-grouped-session", "delete", "therapist-absence"].includes(isModalOpen) && (
                    <EditEvent
                        isAdmin={isAdmin}
                        locale={locale}
                        view={isModalOpen}
                        events={events}
                        event={selectedEvent?.event}
                        updateEvent={selectedEvent?.update}
                        creationType={creationType}
                        therapistsList={therapistsList}
                        therapistData={therapistData}
                        selectedTherapists={selectedTherapists}
                        actions={{
                            hasPermission,
                            handleCreateEventAction,
                            handleUpdateEventAction,
                            handleEventDeleteAction,
                            triggerDeleteEventModal,
                            triggerCloseModal,
                            therapistSelectionHook,
                            batchTherapistSelection,
                            createTherapistAbsenceAction,
                            updateTherapistAbsenceAction,
                            deleteTherapistAbsenceAction
                        }}
                        bookEventFormProps={bookEventFormProps}
                    />
                )}
                {isModalOpen === "view" && (
                    <EventDetails
                        isAdmin={isAdmin}
                        locale={locale}
                        event={selectedEvent?.update || selectedEvent?.event}
                        showCreatedBy={calendarProps?.showCreatedBy}
                        showRecurrence={calendarProps?.showRecurrence}
                        actions={{
                            hasPermission,
                            triggerBookEventModal,
                            triggerDeleteEventModal,
                            therapistSelectionHook,
                            triggerUpdateEventModal,
                            triggerCreateAbsenceModal,
                            triggerUpdateGroupedSessionEventModal,
                            triggerCloseModal
                        }}
                    />
                )}
            </Dialog>
        </MainCard>
    );
};

export default BaseCalendar;
